import {
  useWSModalCustom,
  WSDataItemProps,
  WSIconProps,
  WSInfoBox,
  WSModal,
  WSPillProps
} from "@wingspanhq/fe-component-library";
import { IMember, INewUser } from "@wingspanhq/users/dist/lib/interfaces";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import type { onboardingApiSpec } from "@wingspanhq/wingspan-api-types";
import { DataView } from "../../../shared/components/DataView";
import { selectorAccountType } from "../../../shared/selectors/selectorAccountType";
import { selectorCountryName } from "../../../shared/selectors/selectorCountryName";
import { selectorTaxClassificationLabel } from "../../../shared/selectors/selectorTaxClassificationLabel";
import {
  getTaxIdLabel,
  getTaxIdVerificationAlert
} from "../../../shared/utils/taxId";
import {
  PATH_ONBOARDING_CHANGE,
  PATH_ONBOARDING_VERIFICAIONS_TAX
} from "../../Onboarding";
import { alertMissingDataProps } from "../constants/alerts";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";

type Props = {
  monitoredEntity?: onboardingApiSpec.CustomerEntity;
  user: INewUser;
  member: IMember;
  verificationStatus?: onboardingApiSpec.VerificationStatus;
  missingFields?: string[];
};

function getVerificationStatusPill(
  status: onboardingApiSpec.VerificationStatus
): WSPillProps {
  if (status === "Verified") {
    return {
      theme: "success",
      icon: true,
      text: "Verified"
    };
  }

  if (status === "Pending") {
    return {
      theme: "warning",
      icon: true,
      text: "Verification pending"
    };
  }

  if (status === "UpdateRequired") {
    return {
      theme: "warning",
      icon: true,
      text: "Update required"
    };
  }

  if (status === "Failed") {
    return {
      theme: "error",
      icon: true,
      text: "Failed"
    };
  }

  return {
    theme: "neutral",
    text: "Not verified"
  };
}

const verifiedIcon: WSIconProps = {
  name: "check-circle",
  color: "green400"
};

export const DataViewTaxpayerInformation: React.FC<Props> = ({
  monitoredEntity,
  user,
  member,
  verificationStatus,
  missingFields
}) => {
  const featureFlags = useFeatureFlags();

  const history = useHistory();
  const modalInfo = useWSModalCustom(ModalInfo);

  const country = useMemo(
    () => monitoredEntity?.country || member.profile.address?.country || "US",
    [member.profile.address?.country, monitoredEntity?.country]
  );

  const accountType = useMemo(
    () => selectorAccountType(monitoredEntity, member),
    [member, monitoredEntity]
  );

  const isVerified = useMemo(
    () => verificationStatus === "Verified",
    [verificationStatus]
  );

  const nameItems = useMemo((): (WSDataItemProps | null)[] => {
    if (accountType === "Business") {
      return [
        {
          label: "Legal business name",
          value: member.profile.company?.legalBusinessName,
          icon: isVerified ? verifiedIcon : undefined,
          alert: missingFields?.includes("legalBusinessName")
            ? alertMissingDataProps
            : undefined
        }
      ];
    }

    return [
      {
        label: "Legal first name",
        value: user.profile.firstName,
        icon: isVerified ? verifiedIcon : undefined,
        alert: missingFields?.includes("firstName")
          ? alertMissingDataProps
          : undefined
      },
      user.profile.middleName
        ? {
            label: "Middle name",
            value: user.profile.middleName,
            icon: isVerified ? verifiedIcon : undefined
          }
        : null,
      {
        label: "Legal last name",
        value: user.profile.lastName,
        icon: isVerified ? verifiedIcon : undefined,
        alert: missingFields?.includes("lastName")
          ? alertMissingDataProps
          : undefined
      }
    ];
  }, [
    accountType,
    isVerified,
    member.profile.company?.legalBusinessName,
    missingFields,
    user.profile.firstName,
    user.profile.lastName,
    user.profile.middleName
  ]);

  return (
    <DataView
      header={{
        label: "Taxpayer information",
        value: {
          action: {
            label: "Request update",
            onClick: () => {
              if (!verificationStatus || verificationStatus === "None") {
                history.push(PATH_ONBOARDING_VERIFICAIONS_TAX);
              } else if (verificationStatus === "Verified") {
                history.push(PATH_ONBOARDING_CHANGE);
              } else {
                modalInfo.open({
                  onContinue: () => {
                    history.push(PATH_ONBOARDING_CHANGE);
                  }
                });
              }
            }
          },
          pill: verificationStatus
            ? getVerificationStatusPill(verificationStatus)
            : undefined
        }
      }}
      data={[
        {
          label: "Country of tax residency",
          value: selectorCountryName(country),
          icon: isVerified ? verifiedIcon : undefined
        },
        {
          label: "Business type",
          value: accountType,
          icon: isVerified ? verifiedIcon : undefined
        },
        {
          label: "Federal tax classification",
          value:
            accountType === "Individual"
              ? "Sole proprietorship / Single member LLC"
              : member.profile.company?.structure
                ? selectorTaxClassificationLabel(
                    member.profile.company.structure
                  )
                : undefined,
          icon: isVerified ? verifiedIcon : undefined
        },
        {
          label: getTaxIdLabel(accountType, country),
          value: "•••••••••",
          icon: isVerified ? verifiedIcon : undefined,
          alert: getTaxIdVerificationAlert({
            country,
            status: verificationStatus,
            missingFields,
            isPendingPaused: featureFlags.data?.is1099FilingPaused
          })
        },
        ...nameItems
      ]}
    />
  );
};

const ModalInfo: React.FC<{
  onClose: () => void;
  onContinue: () => void;
}> = props => (
  <WSModal
    onClose={props.onClose}
    divider
    size="S"
    title="Request update to taxpayer information"
    actions={{
      alignment: "fill",
      buttons: [
        {
          kind: "Secondary",
          label: "Cancel",
          onClick: props.onClose
        },
        {
          label: "Continue",
          onClick: props.onContinue
        }
      ]
    }}
  >
    <WSInfoBox title="What to expect:">
      <ol>
        <li>
          You will need to complete and submit your updated information to be
          re-verified.
        </li>
        <li>Your previous information will be discarded.</li>
        <li>
          Your client(s) will be informed of any tax-related changes to your
          profile for compliance and tax reporting purposes.
        </li>
      </ol>
    </WSInfoBox>
  </WSModal>
);
